import classNames from 'classnames'
import React from 'react'
import { useTranslations } from '../../hooks/useTranslations'
import { isNullOrWhiteSpace } from '../../utilities/string'
import time from '../../utilities/time'
import { Paragraph } from '../Paragraph'

export interface KpiClass {
  kpiValues?: KpiValue[]
  name?: string
}

export interface KpiValue {
  title: string
  value: string
}

export interface KpiBlockProps {
  footnotes?: string[]
  kpiClasses?: KpiClass[]
  lastUpdate?: string
}

export const KpiBlock = ({ footnotes, lastUpdate, kpiClasses }: KpiBlockProps) => {
  const translations = useTranslations()
  const update = !!lastUpdate ? time.toShortDate(lastUpdate) : lastUpdate

  return (
    <div
      className={classNames(
        'col-span-full flex flex-col gap-20',
        'sm:col-start-2 sm:col-span-4',
        'md:col-start-1 md:col-span-6 md:gap-30',
        'lg:col-start-2 lg:col-span-6',
        'xl:col-start-2 xl:col-span-8 xl:mx-75',
        'xxl:col-start-4 xxl:col-span-8'
      )}
    >
      {kpiClasses?.map((kpiClass, index) => (
        <KpiSection key={index} {...kpiClass} />
      ))}

      {!isNullOrWhiteSpace(lastUpdate) && (
        <div
          className={classNames('flex font-t-light text-t-200 text-black/60', {
            'md:justify-center':
              (kpiClasses?.filter(kpiClass => (kpiClass.kpiValues?.length ?? 0) > 3)
                .length ?? 0) === 0,
          })}
        >
          {translations.lastUpdated ?? 'Last updated'}: {update}
        </div>
      )}

      {!!footnotes?.length && (
        <div className={classNames('flex flex-col gap-15')}>
          {footnotes.map((footnote, index) => (
            <Paragraph
              className="font-t-light text-t-200 text-black/60 lg:text-t-300"
              key={index}
            >
              {footnote}
            </Paragraph>
          ))}
        </div>
      )}
    </div>
  )
}

export const KpiSection = ({ kpiValues }: KpiClass) => (
  <div
    className={classNames('grid grid-cols-2 gap-20', {
      'md:grid-cols-4': (kpiValues?.length ?? 0) > 3,
      'md:flex md:flex-row md:items-center md:justify-center':
        (kpiValues?.length ?? 0) < 4,
    })}
  >
    {kpiValues?.map((kpiValue, index) => (
      <div
        key={index}
        className={classNames('flex flex-col bg-black/[0.03] p-15 rounded-8 min-h-120', {
          'md:w-1/4': (kpiValues?.length ?? 0) < 4,
        })}
      >
        <Paragraph className="font-t text-t-100 md:text-t-300 text-secondary-dark">
          {kpiValue.title}
        </Paragraph>

        <Paragraph className="font-t text-t-700 md:text-t-800 text-primary">
          {kpiValue.value}
        </Paragraph>
      </div>
    ))}
  </div>
)
